import { useState, useEffect, useRef, useContext } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Form, Formik, setNestedObjectValues } from 'formik';
import ReassignSubmissionSummary from './ReassignSubmissionSummary';
import {
	REASSIGN_FIELD_KEYS,
	REASSIGN_FIELD_STEPS,
	REASSIGN_FIELDS,
	REASSIGN_STEPS,
} from './_const';
import { useHistory } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import UserInformationHeader from './UserInformationHeader';
import FixedBottomNavigation from 'pages/AnnualUserAccessReview/components/FixedBottomNavigation';
import {
	extractValuesArray,
	extractValuesObject,
	navigate,
	processStaffDataForTable,
	hasErrors,
} from 'pages/AnnualUserAccessReview/_util';
import PersonSearch from 'components/UI/Input/PersonSearch/PersonSearch';
import { deepCloneObject } from 'store/utility';
import axiosInstance from '../../../../services/axios/axios';
import classes from 'pages/UserAccessReview/UserAccessReview.module.scss';
import SelectUsers from 'pages/AnnualUserAccessReview/components/SelectUsers';
import {
	fetchBasicStaffList,
	getUserData,
} from 'pages/AnnualUserAccessReview/_api';
import { UserContext } from 'context/User/UserContext';
import Box from '@mui/material/Box';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';
import withQueryCheck from './withQueryCheck';
import PropTypes from 'prop-types';
import { NAVIGATION_ACTIONS } from 'pages/AnnualUserAccessReview/_const';
import Yup from 'utils/validation/yup-extensions';

const ReassignForm = ({ initialValuesFromParams, open = false }) => {
	const [activeStep, setActiveStep] = useState(0);
	const [stepProgress, setStepProgress] = useState(0);
	const history = useHistory();
	const formikRef = useRef();

	const [userData, setUserData] = useState(undefined);
	const [sRows, setRows] = useState([]);
	const [sErrorMessage, setErrorMessage] = useState('');
	const [sIsSearching, setIsSearching] = useState(false);
	const [selectedManager, setSelectedManager] = useState();
	const [isValid, setIsValid] = useState(true);
	const [sIsLoading, setIsLoading] = useState(true);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const contextUser = useContext(UserContext);
	const currentManager =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;
	const [disableNext, SetDisableNext] = useState(true);
	const [disableSubmit, SetDisableSubmit] = useState(true);
	const [userToEditFIID, setUserToEditFIID] = useState();

	const injectFromParams = obj => {
		if (initialValuesFromParams.length > 0) {
			obj[REASSIGN_FIELD_KEYS.USERS_LIST] = initialValuesFromParams.map(
				o => ({
					...o,
					isSelected: true,
				})
			);
		}
		return obj;
	};

	/**
	 * watch `initialValuesFromParams` and if query params are present, transition to next step automatically
	 */
	useEffect(() => {
		let active = true;

		if (active && initialValuesFromParams.length > 0) {
			setStepProgress(1);
			setActiveStep(1);
		}

		return () => {
			active = false;
		};
	}, [initialValuesFromParams]);

	const [sStaff, setStaff] = useState([]);
	useEffect(() => {
		let active = true;

		async function fetchApi() {
			//fetch the base job category info
			const response = await fetchBasicStaffList(currentManager.ID);
			const { values } = response;
			const data = processStaffDataForTable(values);
			//set the state
			setStaff(data);
			setIsLoading(false);
		}

		if (active && currentManager.ID) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [currentManager]);

	const CancelForm = () => {
		let tempData = userData;
		setUserData(tempData);
	};

	const GoHome = () => {
		history.goBack();
	};

	const onNavigate = type => {
		let newStep = navigate(
			type,
			activeStep,
			stepLabels.length,
			CancelForm,
			GoHome
		);
		SetDisableNext(true);
		setStepProgress(Math.max(newStep, stepProgress));
		setActiveStep(newStep);
	};

	const stepLabels = extractValuesArray(REASSIGN_STEPS);

	const initialValues = injectFromParams(
		extractValuesObject(REASSIGN_FIELDS, 'defaultValue')
	);

	const validationSchema = Yup.object().shape(
		extractValuesObject(REASSIGN_FIELDS, 'validationSchema')
	);

	if (initialValues) {
		formikRef?.current?.setTouched(
			setNestedObjectValues(initialValues, true)
		);
	}

	// Call GetUserData API on load to load user information
	useEffect(() => {
		if (userToEditFIID) {
			getUserData(userToEditFIID).then(resp => {
				let data = resp;
				if (data) {
					setUserData(data);
					SetDisableNext(false);
				} else {
					//Users who do not have staff info
					SetDisableNext(true);
				}
			});
		}
	}, [userToEditFIID]);

	/**
	 * Helper method filtering rows stored in local state against user input. Adds a property to row objects
	 * used to determine visibility of row.
	 *
	 * @param {Event} e event object raised on input change.
	 * @returns void
	 */
	const indexRows = e => {
		let rowsCopy = deepCloneObject(sRows);
		rowsCopy.forEach(row => {
			const indexed = Object.keys(row.Cells).some(
				key =>
					row.Cells[key].Value &&
					row.Cells[key].Value.toLowerCase().includes(e.target.value)
			);
			row.isDisplayed = indexed;
		});
		setRows(rowsCopy);
	};

	/**
	 * Asynchronous handler raised on user action. Sets session variables according to selected user information.
	 *
	 * @param {Object} packet object representing clicked row object.
	 *
	 * @returns void
	 */
	const onRowClickedHandler = async packet => {
		if (packet.payload.Cells.isManager.Value !== 'false') {
			if (currentManager.ESOID === packet.payload.Cells.F_ESO_ID.Value) {
				setErrorMessage('The selected user is the current manager');
				setSelectedManager();
				setIsValid(false);
				SetDisableNext(true);
				SetDisableSubmit(true);
			} 
			else if (userData.esoId === packet.payload.Cells.F_ESO_ID.Value) {
				setErrorMessage('The selected manager is the current user');
				setSelectedManager();
				setIsValid(false);
				SetDisableNext(true);
				SetDisableSubmit(true);
			} 
			else{
				setSelectedManager(packet.payload.Cells);
				setIsValid(true);
				SetDisableNext(false);
				SetDisableSubmit(false);
			}
		} else {
			setSelectedManager();
			setErrorMessage('The selected user is not a manager');
			setIsValid(false);
			SetDisableNext(true);
			SetDisableSubmit(true);
		}
	};

	const renderStepContent = (step, formik) => {
		switch (step) {
			//select one staff member
			case 0:
				return (
					<SelectUsers
						singleSelection={true}
						key={'step_' + activeStep}
						name={REASSIGN_FIELD_KEYS.USERS_LIST}
						formik={formik}
						directReports={sStaff.filter(
							dr => dr.accountCategoryName === 'NON_EMPLOYEE'
						)}
						selectUser={setUserToEditFIID}
					/>
				);
			//Select the new manager
			case 1:
				return (
					<div>
						<UserInformationHeader {...{ formik, userData }} />
						<PersonSearch
							className={classes['person-search']}
							label='Manager Lookup'
							dataTable={{
								onTextFieldChangedHandler: indexRows,
								onRowClickedHandler: onRowClickedHandler,
								rows: sRows.filter(
									row => row?.isDisplayed !== false
								),
							}}
							errorMessage={sErrorMessage}
							isLoading={sIsSearching}
							multiPerson={false}
							onButtonClickedHandler={payload => {
								setIsSearching(true);

								axiosInstance
									.post(
										'api/Components/PersonSearch',
										payload
									)
									.then(response => {
										switch (response.status) {
											case 200:
												switch (
													response.data.responseCode
												) {
													case 200:
														setRows(
															response.data
																.payload
														);
														setErrorMessage('');
														break;
													default:
														setErrorMessage(
															response.data
																.messages[0]
																.Value
														);
												}
												break;
											default:
												break;
										}
									})
									.catch(error => {
										setErrorMessage(
											error &&
												error.response &&
												error.response.data
												? error.response.data.payload
												: error.message
										);
									})
									.finally(() => {
										setIsSearching(false);
									});
							}}
							requestTarget='REQUESTEE'
							withoutFormik={true}
						></PersonSearch>
					</div>
				);
			//Submit
			case 2:
				setIsSubmitted(true);
				return (
					<ReassignSubmissionSummary
						key={'step_' + activeStep}
						{...{ userData, selectedManager, currentManager }}
						name={REASSIGN_FIELD_KEYS.SUBMIT}
						formik={formik}
					/>
				);
			default:
				return <>Default</>;
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			validateOnMount
			innerRef={formikRef}
		>
			{formik => (
				<Form>
					<Stepper activeStep={activeStep} alternativeLabel>
						{stepLabels.map((label, index) => {
							const labelProps = {};
							if (!isValid && index !== 2) {
								labelProps.optional = (
									<Typography variant='caption' color='error'>
										Information Missing
									</Typography>
								);

								labelProps.error = true;
							}
							return (
								<Step key={'step_' + index}>
									<StepLabel
										onClick={() => {
											setErrorMessage();
											setSelectedManager();
											SetDisableSubmit(true);
											if (
												activeStep > index &&
												!isSubmitted
											) {
												setActiveStep(index);
											}
										}}
										{...labelProps}
									>
										{label}
									</StepLabel>
								</Step>
							);
						})}
					</Stepper>
					<Box sx={{ mt: 3 }}>
						<WithLoadingHandler
							isLoading={sIsLoading}
							showChildren={!sIsLoading}
						>
							{renderStepContent(activeStep, formik)}
						</WithLoadingHandler>
					</Box>
					<FixedBottomNavigation
						onChange={type => {
							if (type === NAVIGATION_ACTIONS.NEXT) {
								REASSIGN_FIELD_STEPS[activeStep].forEach(s => {
									if (s) {
										formik.setFieldTouched(s, true);
									}
								});
							}
							if (formik.errors.usersList === undefined) {
								onNavigate(type, formik);
							}
						}}
						currentStep={activeStep}
						stepCount={stepLabels.length}
						hasErrors={hasErrors(formik)}
						open={open}
						enableHome={false}
						enableRestart={false}
						isSubmitting={formik.isSubmitting}
						keepNextDisabled={disableNext}
						keepSubmitDisabled={disableSubmit}
						isSubmitted={isSubmitted}
					/>
				</Form>
			)}
		</Formik>
	);
};

ReassignForm.propTypes = {
	initialValuesFromParams: PropTypes.arrayOf(PropTypes.object),
	open: PropTypes.bool,
};

export default withQueryCheck(ReassignForm);
